import { z } from 'zod';

import { emailSchema } from './auth';

export const sendStatusSchema = z.enum([
  'queued',
  'sent',
  'delivered',
  'deferred',
  'bounced',
  'complained',
  'opened',
  'clicked',
  'failed',
]);

export const sendRequestSchema = z.object({
  to: emailSchema,
  toName: z.string().max(200).optional(),
  variables: z.record(z.unknown()).default({}),
  providerConnectionId: z.string().uuid().optional(),
  sendAt: z.string().datetime().optional(),
  tracking: z
    .object({
      // No defaults — leaving a field undefined lets the API resolve it
      // against the template's `defaultTracking` and finally fall back to the
      // platform default of true.
      open: z.boolean().optional(),
      click: z.boolean().optional(),
    })
    .optional(),
  idempotencyKey: z.string().min(8).max(128).optional(),
  /**
   * BCP-47 locale code (`en`, `es`, `pt-br`, …). When set, the renderer prefers
   * `template.subjectByLocale[locale]` over `variables.subject` and the
   * template's display name. No effect on body content — the AST is single-locale
   * per version; localize the AST itself (or use Handlebars + variables) for
   * deeper translation.
   */
  locale: z.string().min(2).max(20).optional(),
});

export const sendEventSchema = z.object({
  id: z.string().uuid(),
  templateId: z.string().uuid(),
  versionId: z.string().uuid(),
  providerConnectionId: z.string().uuid(),
  providerMessageId: z.string().nullable(),
  recipientEmailHash: z.string(),
  status: sendStatusSchema,
  latencyMs: z.number().int().nonnegative().nullable(),
  occurredAt: z.string().datetime(),
});

export type SendStatus = z.infer<typeof sendStatusSchema>;
export type SendRequestInput = z.infer<typeof sendRequestSchema>;
export type SendEvent = z.infer<typeof sendEventSchema>;
